import { t as uiText } from '../i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import type { WorkspaceNotificationChannel } from '../features/workspace/types';
import { deleteNotificationChannel, registerSlackIncomingWebhook, sendTestNotification, startSlackInstall, updateWorkspaceNotificationRules, upsertSlackNotificationChannel } from '../api/notifications';

function mapNotificationChannel(id: string, cd: any): WorkspaceNotificationChannel {
  return {
    id,
    type: cd?.type,
    label: (typeof cd?.label === 'string' ? cd.label : '') as string,
    enabled: cd?.enabled === true,
    teamId: (typeof cd?.teamId === 'string' ? cd.teamId : undefined) as any,
    teamName: (typeof cd?.teamName === 'string' ? cd.teamName : undefined) as any,
    source: cd?.source === 'manual' ? 'manual' : undefined,
  };
}

function channelSubtitle(c: WorkspaceNotificationChannel, slackTeamNameById: Record<string, string>): string {
  const teamName = c.teamName || (c.teamId ? slackTeamNameById[c.teamId] : undefined);
  if (c.source === 'manual' && !teamName) {
    return uiText('Incoming Webhook');
  }
  if (c.teamId) {
    return uiText('Slack Workspace: {{v0}}', { v0: teamName || c.teamId });
  }
  return `ID: ${c.id}`;
}

interface NotificationSettingsModalProps {
  onClose: () => void;
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({ onClose }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { refresh } = useWorkspaceContext();
  const { can } = useWorkspaceEntitlements();

  // 管理者権限チェック
  const { data: isAdmin, isLoading: isAdminLoading } = useIsWorkspaceAdminQuery(
    routeWorkspaceId ?? '',
    !!routeWorkspaceId
  );

  const [notifyOnCreate, setNotifyOnCreate] = useState<boolean>(false);
  const [notifyOnUpdate, setNotifyOnUpdate] = useState<boolean>(false);
  const [threadNotifyOnCreate, setThreadNotifyOnCreate] = useState<boolean>(false);
  const [threadNotifyOnAppend, setThreadNotifyOnAppend] = useState<boolean>(false);
  const [threadNotifyOnComment, setThreadNotifyOnComment] = useState<boolean>(false);
  const [threadNotifyOnStatusChange, setThreadNotifyOnStatusChange] = useState<boolean>(false);
  const [emailNotifyDefault, setEmailNotifyDefault] = useState<boolean>(true);
  const [settingsTab, setSettingsTab] = useState<'slack' | 'email'>('slack');

  const [boardNotificationChannels, setBoardNotificationChannels] = useState<WorkspaceNotificationChannel[]>([]);
  const [threadNotificationChannels, setThreadNotificationChannels] = useState<WorkspaceNotificationChannel[]>([]);
  const [notifSaving, setNotifSaving] = useState<boolean>(false);
  const [rulesUpdating, setRulesUpdating] = useState<boolean>(false);
  const [slackInstallBusy, setSlackInstallBusy] = useState<boolean>(false);
  const [slackOauthAwaiting, setSlackOauthAwaiting] = useState<boolean>(false);
  const [slackOauthBanner, setSlackOauthBanner] = useState<{ ok: boolean; message: string } | null>(null);
  const [slackOauthTarget, setSlackOauthTarget] = useState<'boards' | 'threads' | null>(null);
  const slackOauthBaselineRef = useRef<{ target: 'boards' | 'threads'; ids: Set<string> } | null>(null);
  const slackOauthAwaitingRef = useRef<boolean>(false);
  const [slackTeams, setSlackTeams] = useState<Record<string, { teamId: string; teamName?: string }>>({});
  const [boardWebhookLabel, setBoardWebhookLabel] = useState('');
  const [boardWebhookUrl, setBoardWebhookUrl] = useState('');
  const [threadWebhookLabel, setThreadWebhookLabel] = useState('');
  const [threadWebhookUrl, setThreadWebhookUrl] = useState('');
  const [manualWebhookBusyTarget, setManualWebhookBusyTarget] = useState<'boards' | 'threads' | null>(null);

  const boardRulesOn = notifyOnCreate || notifyOnUpdate;
  const threadRulesOn = threadNotifyOnCreate || threadNotifyOnAppend || threadNotifyOnComment || threadNotifyOnStatusChange;

  const boardEnabledChannelCount = useMemo(() => {
    return boardNotificationChannels.filter((c) => c.enabled).length;
  }, [boardNotificationChannels]);

  const threadEnabledChannelCount = useMemo(() => {
    return threadNotificationChannels.filter((c) => c.enabled).length;
  }, [threadNotificationChannels]);

  const setSlackOauthAwaitingSafe = (v: boolean) => {
    slackOauthAwaitingRef.current = v;
    setSlackOauthAwaiting(v);
  };

  useEffect(() => {
    const loadNotificationData = async () => {
      if (!routeWorkspaceId) return;
      try {
        const wsSnap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (wsSnap.exists()) {
          const data = wsSnap.data() as any;
          setNotifyOnCreate(data?.notificationRules?.onCreate === true);
          setNotifyOnUpdate(data?.notificationRules?.onUpdate === true);
          setThreadNotifyOnCreate(data?.notificationRules?.threadOnCreate === true);
          setThreadNotifyOnAppend(data?.notificationRules?.threadOnAppend === true);
          setThreadNotifyOnComment(data?.notificationRules?.threadOnComment === true);
          setThreadNotifyOnStatusChange(data?.notificationRules?.threadOnStatusChange === true);
          setEmailNotifyDefault(data?.notificationRules?.emailNotifyDefault !== false);
        } else {
          setNotifyOnCreate(false);
          setNotifyOnUpdate(false);
          setThreadNotifyOnCreate(false);
          setThreadNotifyOnAppend(false);
          setThreadNotifyOnComment(false);
          setThreadNotifyOnStatusChange(false);
          setEmailNotifyDefault(true);
        }

        // ✅ boards: notificationChannels はサブコレクションへ移行済み
        try {
          const channelsSnap = await getDocs(collection(db, 'workspaces', routeWorkspaceId, 'notificationChannels'));
          setBoardNotificationChannels(
            channelsSnap.docs.map((d) => mapNotificationChannel(d.id, d.data()))
          );
        } catch (e) {
          // フォールバック（旧: workspace直下の配列）
          const wsSnap2 = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
          const data2 = wsSnap2.exists() ? (wsSnap2.data() as any) : {};
          const ch = Array.isArray(data2?.notificationChannels) ? data2.notificationChannels : [];
          setBoardNotificationChannels(
            ch
              .map((c: any) => mapNotificationChannel(typeof c?.id === 'string' ? c.id : '', c))
              .filter((c) => !!c.id)
          );
        }

        // threads: threadNotificationChannels（新規）
        try {
          const channelsSnap = await getDocs(collection(db, 'workspaces', routeWorkspaceId, 'threadNotificationChannels'));
          setThreadNotificationChannels(
            channelsSnap.docs.map((d) => mapNotificationChannel(d.id, d.data()))
          );
        } catch {
          setThreadNotificationChannels([]);
        }
      } catch (e) {
        console.error('❌ 通知設定の読み込みに失敗:', e);
      }
    };

    loadNotificationData();
  }, [routeWorkspaceId]);

  const reloadChannels = async (target: 'boards' | 'threads') => {
    if (!routeWorkspaceId) return;
    try {
      const colName = target === 'threads' ? 'threadNotificationChannels' : 'notificationChannels';
      const channelsSnap = await getDocs(collection(db, 'workspaces', routeWorkspaceId, colName));
      const list = channelsSnap.docs.map((d) => mapNotificationChannel(d.id, d.data()));
      if (target === 'threads') {
        setThreadNotificationChannels(list);
      } else {
        setBoardNotificationChannels(list);
      }
    } catch {}
  };

  const applyNotificationRulesPatch = async (patch: {
    onCreate?: boolean;
    onUpdate?: boolean;
    threadOnCreate?: boolean;
    threadOnAppend?: boolean;
    threadOnComment?: boolean;
    threadOnStatusChange?: boolean;
    emailNotifyDefault?: boolean;
  }) => {
    if (!routeWorkspaceId) {
      alert(uiText("Workspace ID not found"));
      return;
    }
    if (!isAdmin) {
      alert(uiText("Only admins can change notification settings"));
      return;
    }
    if (rulesUpdating) return;

    const prev = {
      onCreate: notifyOnCreate,
      onUpdate: notifyOnUpdate,
      threadOnCreate: threadNotifyOnCreate,
      threadOnAppend: threadNotifyOnAppend,
      threadOnComment: threadNotifyOnComment,
      threadOnStatusChange: threadNotifyOnStatusChange,
      emailNotifyDefault,
    };

    const next = {
      ...prev,
      ...patch,
    };

    setNotifyOnCreate(next.onCreate);
    setNotifyOnUpdate(next.onUpdate);
    setThreadNotifyOnCreate(next.threadOnCreate);
    setThreadNotifyOnAppend(next.threadOnAppend);
    setThreadNotifyOnComment(next.threadOnComment);
    setThreadNotifyOnStatusChange(next.threadOnStatusChange);
    setEmailNotifyDefault(next.emailNotifyDefault);

    setRulesUpdating(true);
    try {
      await updateWorkspaceNotificationRules({ workspaceId: routeWorkspaceId, ...patch });
      await refresh();
    } catch (e: any) {
      console.error('通知ルール更新に失敗:', e);
      const hasThreadPatch =
        Object.prototype.hasOwnProperty.call(patch || {}, 'threadOnCreate') ||
        Object.prototype.hasOwnProperty.call(patch || {}, 'threadOnAppend') ||
        Object.prototype.hasOwnProperty.call(patch || {}, 'threadOnComment') ||
        Object.prototype.hasOwnProperty.call(patch || {}, 'threadOnStatusChange');

      const isOldBackendError =
        e?.code === 'functions/invalid-argument' &&
        typeof e?.message === 'string' &&
        e.message.includes('onCreate or onUpdate is required');

      // バックエンドが旧実装のまま（thread用ルール未対応）でも、UIが壊れないようにFirestoreへ直接パッチするフォールバック
      if (hasThreadPatch && isOldBackendError) {
        try {
          const wsRef = doc(db, 'workspaces', routeWorkspaceId);
          const updatePayload: Record<string, any> = { updatedAt: serverTimestamp() };
          if (Object.prototype.hasOwnProperty.call(patch, 'threadOnCreate')) {
            updatePayload['notificationRules.threadOnCreate'] = patch.threadOnCreate;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'threadOnAppend')) {
            updatePayload['notificationRules.threadOnAppend'] = patch.threadOnAppend;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'threadOnComment')) {
            updatePayload['notificationRules.threadOnComment'] = patch.threadOnComment;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'threadOnStatusChange')) {
            updatePayload['notificationRules.threadOnStatusChange'] = patch.threadOnStatusChange;
          }

          await updateDoc(wsRef, updatePayload);
          await refresh();

          return;
        } catch (fallbackErr: any) {
          // noop
        }
      }
      setNotifyOnCreate(prev.onCreate);
      setNotifyOnUpdate(prev.onUpdate);
      setThreadNotifyOnCreate(prev.threadOnCreate);
      setThreadNotifyOnAppend(prev.threadOnAppend);
      setThreadNotifyOnComment(prev.threadOnComment);
      setThreadNotifyOnStatusChange(prev.threadOnStatusChange);
      setEmailNotifyDefault(prev.emailNotifyDefault);
      alert(e?.message || uiText("Couldn't update notification rules"));
    } finally {
      setRulesUpdating(false);
    }
  };

  const handleSlackInstall = async (target: 'boards' | 'threads') => {
    if (!routeWorkspaceId) {
      alert(uiText("Workspace ID not found"));
      return;
    }
    if (!isAdmin) {
      alert(uiText("Only admins can connect Slack"));
      return;
    }

    setSlackInstallBusy(true);
    setSlackOauthAwaitingSafe(true);
    setSlackOauthTarget(target);
    const baselineIds = new Set(
      (target === 'threads' ? threadNotificationChannels : boardNotificationChannels).map((c) => c.id)
    );
    slackOauthBaselineRef.current = { target, ids: baselineIds };
    setSlackOauthBanner({ ok: true, message: uiText("The Slack connection window is open. This screen refreshes automatically when it finishes.") });
    try {
      const { authUrl } = await startSlackInstall({ workspaceId: routeWorkspaceId, origin: window.location.origin, target });
      const features = 'noopener,noreferrer';
      window.open(authUrl, '_blank', features);
    } catch (e: any) {
      console.error('Slack連携開始に失敗:', e);
      alert(e?.message || uiText("Failed to start the Slack connection"));
      setSlackOauthAwaitingSafe(false);
      setSlackOauthTarget(null);
      setSlackOauthBanner({ ok: false, message: e?.message || uiText("Failed to start the Slack connection") });
    } finally {
      setSlackInstallBusy(false);
    }
  };

  const handleRegisterManualWebhook = async (target: 'boards' | 'threads') => {
    if (!routeWorkspaceId) {
      alert(uiText("Workspace ID not found"));
      return;
    }
    if (!isAdmin) {
      alert(uiText("Only admins can connect Slack"));
      return;
    }

    const label = target === 'threads' ? threadWebhookLabel : boardWebhookLabel;
    const webhookUrl = target === 'threads' ? threadWebhookUrl : boardWebhookUrl;
    if (!webhookUrl.trim()) {
      alert(uiText("Enter an Incoming Webhook URL"));
      return;
    }

    setManualWebhookBusyTarget(target);
    setNotifSaving(true);
    try {
      const result = await registerSlackIncomingWebhook({
        workspaceId: routeWorkspaceId,
        target,
        label,
        webhookUrl,
      });
      if (target === 'threads') {
        setThreadWebhookLabel('');
        setThreadWebhookUrl('');
      } else {
        setBoardWebhookLabel('');
        setBoardWebhookUrl('');
      }
      await refresh();
      await reloadChannels(target);
      if (result.testOk) {
        alert(uiText("Incoming Webhook saved. A test notification was sent."));
      } else {
        alert(uiText("Incoming Webhook saved, but the test notification failed."));
      }
    } catch (e: any) {
      console.error('Incoming Webhook の保存に失敗:', e);
      alert(e?.message || uiText("Failed to save the Incoming Webhook"));
    } finally {
      setManualWebhookBusyTarget(null);
      setNotifSaving(false);
    }
  };

  // Firestore（notificationChannels）を購読して自動更新
  useEffect(() => {
    if (!routeWorkspaceId) return;

    const unsub = onSnapshot(
      collection(db, 'workspaces', routeWorkspaceId, 'notificationChannels'),
      (snap) => {
        const next = snap.docs.map((d) => mapNotificationChannel(d.id, d.data()));
        setBoardNotificationChannels(next);

        // 連携待ち中なら、チャネル追加が観測できた時点で成功バナーに切替
        if (slackOauthAwaitingRef.current) {
          const baseline = slackOauthBaselineRef.current;
          if (baseline) {
            const added = next.find((c) => !baseline.ids.has(c.id));
            if (added) {
              if (slackOauthTarget === 'boards' && baseline.target === 'boards') {
                setSlackOauthAwaitingSafe(false);
                setSlackOauthTarget(null);
                setSlackOauthBanner({ ok: true, message: uiText("Slack connection complete. The channel list has been updated.") });
                slackOauthBaselineRef.current = null;
              }
            }
          }
        }
      },
      () => {}
    );

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [routeWorkspaceId, slackOauthTarget]);

  // Firestore（threadNotificationChannels）を購読して自動更新
  useEffect(() => {
    if (!routeWorkspaceId) return;

    const unsub = onSnapshot(
      collection(db, 'workspaces', routeWorkspaceId, 'threadNotificationChannels'),
      (snap) => {
        const next = snap.docs.map((d) => mapNotificationChannel(d.id, d.data()));
        setThreadNotificationChannels(next);

        if (slackOauthAwaitingRef.current) {
          const baseline = slackOauthBaselineRef.current;
          if (baseline) {
            const added = next.find((c) => !baseline.ids.has(c.id));
            if (added) {
              if (slackOauthTarget === 'threads' && baseline.target === 'threads') {
                setSlackOauthAwaitingSafe(false);
                setSlackOauthTarget(null);
                setSlackOauthBanner({ ok: true, message: uiText("Slack connection complete. The channel list has been updated.") });
                slackOauthBaselineRef.current = null;
              }
            }
          }
        }
      },
      () => {}
    );

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [routeWorkspaceId, slackOauthTarget]);

  // Firestore（slackTeams）を購読して teamName を取得
  useEffect(() => {
    if (!routeWorkspaceId) return;
    const unsub = onSnapshot(collection(db, 'workspaces', routeWorkspaceId, 'slackTeams'), (snap) => {
      const next: Record<string, { teamId: string; teamName?: string }> = {};
      snap.docs.forEach((d) => {
        const data = d.data() as any;
        next[d.id] = {
          teamId: d.id,
          teamName: typeof data?.teamName === 'string' ? data.teamName : undefined,
        };
      });
      setSlackTeams(next);
    });
    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [routeWorkspaceId]);

  const slackTeamNameById = useMemo(() => {
    const map: Record<string, string> = {};
    Object.keys(slackTeams).forEach((k) => {
      const t = slackTeams[k];
      if (t?.teamName) map[k] = t.teamName;
    });
    return map;
  }, [slackTeams]);

  const handleUpdateChannelMeta = async (
    target: 'boards' | 'threads',
    channelId: string,
    patch: Partial<WorkspaceNotificationChannel>
  ) => {
    if (!routeWorkspaceId) return;
    if (!isAdmin) return;

    const list = target === 'threads' ? threadNotificationChannels : boardNotificationChannels;
    const current = list.find((c) => c.id === channelId);
    if (!current) return;

    const next = { ...current, ...patch };
    if (target === 'threads') {
      setThreadNotificationChannels((prev) => prev.map((c) => (c.id === channelId ? next : c)));
    } else {
      setBoardNotificationChannels((prev) => prev.map((c) => (c.id === channelId ? next : c)));
    }

    setNotifSaving(true);
    try {
      await upsertSlackNotificationChannel({
        workspaceId: routeWorkspaceId,
        channelId: next.id,
        label: next.label,
        enabled: next.enabled,
        target,
        // 既存secretは維持する（webhookUrl無し）
      });
      await refresh();
      await reloadChannels(target);
    } catch (e: any) {
      console.error('チャネル更新に失敗:', e);
      alert(e?.message || uiText("Failed to update the channel"));
      await refresh();
      await reloadChannels(target);
    } finally {
      setNotifSaving(false);
    }
  };

  const handleDeleteChannel = async (target: 'boards' | 'threads', channelId: string) => {
    if (!routeWorkspaceId) return;
    if (!isAdmin) return;

    if (!confirm(uiText("Delete this channel? (The Secret will be deleted too)"))) return;

    setNotifSaving(true);
    try {
      await deleteNotificationChannel({ workspaceId: routeWorkspaceId, channelId, target });
      if (target === 'threads') {
        setThreadNotificationChannels((prev) => prev.filter((c) => c.id !== channelId));
      } else {
        setBoardNotificationChannels((prev) => prev.filter((c) => c.id !== channelId));
      }
      await refresh();
      alert(uiText("Channel deleted"));
    } catch (e: any) {
      console.error('チャネル削除に失敗:', e);
      alert(e?.message || uiText("Failed to delete the channel"));
    } finally {
      setNotifSaving(false);
    }
  };

  const handleSendTestNotification = async (channelId: string) => {
    if (!routeWorkspaceId) return;
    if (!isAdmin) return;

    setNotifSaving(true);
    try {
      await sendTestNotification({ workspaceId: routeWorkspaceId, channelId, message: uiText("notreu: Test notification") });
      alert(uiText("Test notification sent"));
    } catch (e: any) {
      console.error('テスト通知に失敗:', e);
      alert(e?.message || uiText("Failed to send test notification"));
    } finally {
      setNotifSaving(false);
    }
  };

  // OAuth callback からの postMessage を受けて一覧を更新
  useEffect(() => {
    if (!routeWorkspaceId) return;
    const handlePayload = async (payload: any) => {
      if (!payload || payload.type !== 'slack_oauth_complete') return;
      if (payload.workspaceId !== routeWorkspaceId) return;
      await reloadChannels('boards');
      await reloadChannels('threads');
      setSlackOauthAwaitingSafe(false);
      setSlackOauthTarget(null);
      if (payload.ok === true) {
        setSlackOauthBanner({ ok: true, message: uiText("Slack connection complete. The channel list has been updated.") });
      } else {
        setSlackOauthBanner({ ok: false, message: payload.error ? uiText("Slack connection failed: {{error}}", { error: payload.error }) : uiText("Slack connection failed") });
      }
    };

    const messageHandler = async (ev: MessageEvent) => {
      await handlePayload((ev as any)?.data);
    };

    const storageHandler = async (ev: StorageEvent) => {
      if (ev.key !== 'notreu_slack_oauth_result' || !ev.newValue) return;
      try {
        const parsed = JSON.parse(ev.newValue);
        await handlePayload(parsed?.payload);
      } catch {}
    };

    let bc: BroadcastChannel | null = null;
    const setupBroadcastChannel = () => {
      try {
        if (typeof BroadcastChannel === 'undefined') return;
        bc = new BroadcastChannel('notreu_slack_oauth');
        bc.onmessage = async (ev: MessageEvent) => {
          await handlePayload((ev as any)?.data);
        };
      } catch {
        bc = null;
      }
    };

    window.addEventListener('message', messageHandler);
    window.addEventListener('storage', storageHandler);
    setupBroadcastChannel();

    return () => {
      window.removeEventListener('message', messageHandler);
      window.removeEventListener('storage', storageHandler);
      try {
        if (bc) bc.close();
      } catch {}
    };
  }, [routeWorkspaceId]);

  const renderManualWebhookForm = (target: 'boards' | 'threads') => {
    const label = target === 'threads' ? threadWebhookLabel : boardWebhookLabel;
    const url = target === 'threads' ? threadWebhookUrl : boardWebhookUrl;
    const setLabel = target === 'threads' ? setThreadWebhookLabel : setBoardWebhookLabel;
    const setUrl = target === 'threads' ? setThreadWebhookUrl : setBoardWebhookUrl;
    const busy = manualWebhookBusyTarget === target;
    const inputStyle: React.CSSProperties = {
      width: '100%',
      padding: '8px 10px',
      border: '1px solid #e9ecef',
      borderRadius: '4px',
      fontSize: '13px',
      boxSizing: 'border-box',
    };
    return (
      <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '12px', color: '#666', fontWeight: 700 }}>
          {uiText("If you already have an Incoming Webhook URL")}
        </div>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={uiText("Display name")}
          disabled={busy || notifSaving}
          maxLength={80}
          style={inputStyle}
        />
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={uiText("Incoming Webhook URL")}
          autoComplete="off"
          spellCheck={false}
          disabled={busy || notifSaving}
          style={inputStyle}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => handleRegisterManualWebhook(target)}
            disabled={busy || notifSaving || !url.trim()}
            style={{
              padding: '8px 12px',
              backgroundColor: busy || notifSaving || !url.trim() ? '#ccc' : 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: busy || notifSaving || !url.trim() ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
            }}
          >
            {busy ? uiText("Saving...") : uiText("Register")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          width: '90%',
          maxWidth: '640px',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>
          {`@keyframes notreuSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
        <h2
          style={{
            margin: '0 0 18px 0',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            textAlign: 'center',
          }}
        >
          {uiText("Notification settings")}
        </h2>

        {slackOauthBanner && (
          <div
            style={{
              marginBottom: '12px',
              padding: '10px 12px',
              borderRadius: '6px',
              border: `1px solid ${slackOauthBanner.ok ? 'var(--workspace-primary, #cfae91)' : '#ffd6d6'}`,
              backgroundColor: slackOauthBanner.ok ? 'var(--workspace-border-soft, #f5f0eb)' : '#fff5f5',
              color: '#333',
              fontSize: '12px',
            }}
          >
            {slackOauthBanner.message}
          </div>
        )}

        {!routeWorkspaceId && (
          <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
            {uiText("Please select a workspace")}
          </div>
        )}

        {!!routeWorkspaceId && (
          <>
            {isAdminLoading ? (
              <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                {uiText("Verifying permissions...")}
              </div>
            ) : !isAdmin ? (
              <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                {uiText("Only admins can change notification settings")}
              </div>
            ) : (
              <div style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {(['slack', 'email'] as const).map((tab) => {
                    const selected = settingsTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setSettingsTab(tab)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 4,
                          border: selected ? '1px solid var(--workspace-primary, #cfae91)' : '1px solid #e9ecef',
                          backgroundColor: selected ? 'var(--workspace-primary, #cfae91)' : '#fff',
                          color: selected ? '#fff' : '#495057',
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {tab === 'slack' ? uiText("Slack") : uiText("Email")}
                      </button>
                    );
                  })}
                </div>
                {settingsTab === 'slack' && (<>
                {can('notification.page') && (<>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 900 }}>{uiText("Page notifications")}</div>

                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* ページ: 新規作成時に通知 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify when created")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: notifyOnCreate ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {notifyOnCreate ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ onCreate: !notifyOnCreate })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: notifyOnCreate ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: notifyOnCreate ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  {/* ページ: 更新時に通知 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify on update")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: notifyOnUpdate ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {notifyOnUpdate ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ onUpdate: !notifyOnUpdate })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: notifyOnUpdate ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: notifyOnUpdate ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '14px', fontSize: '12px', color: '#666', fontWeight: 700 }}>{uiText("Linked channel (pages)")}</div>

                {boardRulesOn && boardEnabledChannelCount === 0 && (
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      backgroundColor: '#fff5f5',
                      border: '1px solid #ffd6d6',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#333',
                      fontWeight: 700,
                    }}
                  >
                    {uiText("No valid notification target. Set one from the Slack integration.")}
                  </div>
                )}

                {boardNotificationChannels.length === 0 ? (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    {uiText("No channels yet. Add one from the Slack integration.")}
                  </div>
                ) : (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {boardNotificationChannels.map((c) => (
                      <div
                        key={c.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px',
                          backgroundColor: '#fff',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#333' }}>
                            {c.label || 'Slack'}
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999', fontWeight: 600 }}>
                              {c.type}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {channelSubtitle(c, slackTeamNameById)}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: c.enabled ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: 700 }}>
                            {c.enabled ? 'ON' : 'OFF'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateChannelMeta('boards', c.id, { enabled: !c.enabled })}
                            disabled={notifSaving}
                            aria-busy={notifSaving}
                            style={{
                              width: '48px',
                              height: '24px',
                              backgroundColor: c.enabled ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                              border: 'none',
                              borderRadius: '12px',
                              cursor: notifSaving ? 'not-allowed' : 'pointer',
                              position: 'relative',
                              transition: 'background-color 0.2s ease',
                              opacity: notifSaving ? 0.7 : 1,
                            }}
                            aria-label={uiText("Make the channel {{v0}}", { v0: c.enabled ? uiText("Disabled") : uiText("Enabled") })}
                          >
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#ffffff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: c.enabled ? '26px' : '2px',
                                transition: 'left 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }}
                            />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleSendTestNotification(c.id)}
                          disabled={notifSaving}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: notifSaving ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: notifSaving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 800,
                          }}
                        >
                          {uiText("Test notification")}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteChannel('boards', c.id)}
                          disabled={notifSaving}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: notifSaving ? '#ccc' : '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: notifSaving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 800,
                          }}
                        >
                          {uiText("Delete")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                </>)}

                {can('notification.slack') && (<>
                <div style={{ marginTop: '14px', fontSize: '12px', color: '#666', fontWeight: 700 }}>{uiText("Slack integration (add a page notification channel)")}</div>

                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {uiText("After signing in on the Slack screen, select a channel to connect. Depending on your Slack workspace settings, an owner or admin may need to approve the app.")}
                  </div>
                  {slackOauthAwaiting && slackOauthTarget === 'boards' && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {uiText("After linking, this screen updates automatically (it may take a few seconds to appear).")}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => handleSlackInstall('boards')}
                      disabled={slackInstallBusy || notifSaving}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: slackInstallBusy || notifSaving ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: slackInstallBusy || notifSaving ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                      }}
                    >
                      {slackInstallBusy && slackOauthTarget === 'boards' ? uiText("Starting up...") : uiText("Open Slack connection")}
                    </button>
                  </div>
                </div>
                {renderManualWebhookForm('boards')}

                </>)}

                {can('notification.thread') && (<>
                <div style={{ margin: '18px 0', borderTop: '1px solid #e9ecef' }} />

                <div style={{ fontSize: '12px', color: '#666', fontWeight: 900 }}>{uiText("Thread notifications")}</div>

                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify when created")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: threadNotifyOnCreate ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {threadNotifyOnCreate ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ threadOnCreate: !threadNotifyOnCreate })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: threadNotifyOnCreate ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: threadNotifyOnCreate ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify when content is added")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: threadNotifyOnAppend ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {threadNotifyOnAppend ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ threadOnAppend: !threadNotifyOnAppend })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: threadNotifyOnAppend ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: threadNotifyOnAppend ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify when a comment is added")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: threadNotifyOnComment ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {threadNotifyOnComment ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ threadOnComment: !threadNotifyOnComment })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: threadNotifyOnComment ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: threadNotifyOnComment ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}>
                      {uiText("Notify when the status changes")}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: threadNotifyOnStatusChange ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                        {threadNotifyOnStatusChange ? 'ON' : 'OFF'}
                      </span>
                      <button
                        type="button"
                        onClick={() => applyNotificationRulesPatch({ threadOnStatusChange: !threadNotifyOnStatusChange })}
                        disabled={rulesUpdating || notifSaving}
                        aria-busy={rulesUpdating}
                        style={{
                          width: '48px',
                          height: '24px',
                          backgroundColor: threadNotifyOnStatusChange ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: threadNotifyOnStatusChange ? '26px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '14px', fontSize: '12px', color: '#666', fontWeight: 700 }}>{uiText("Linked channel (threads)")}</div>

                {threadRulesOn && threadEnabledChannelCount === 0 && (
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      backgroundColor: '#fff5f5',
                      border: '1px solid #ffd6d6',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#333',
                      fontWeight: 700,
                    }}
                  >
                    {uiText("No valid notification target. Set one from the Slack integration.")}
                  </div>
                )}

                {threadNotificationChannels.length === 0 ? (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    {uiText("No channels yet. Add one from the Slack integration.")}
                  </div>
                ) : (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {threadNotificationChannels.map((c) => (
                      <div
                        key={c.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px',
                          backgroundColor: '#fff',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#333' }}>
                            {c.label || 'Slack'}
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999', fontWeight: 600 }}>
                              {c.type}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {channelSubtitle(c, slackTeamNameById)}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: c.enabled ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: 700 }}>
                            {c.enabled ? 'ON' : 'OFF'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateChannelMeta('threads', c.id, { enabled: !c.enabled })}
                            disabled={notifSaving}
                            aria-busy={notifSaving}
                            style={{
                              width: '48px',
                              height: '24px',
                              backgroundColor: c.enabled ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                              border: 'none',
                              borderRadius: '12px',
                              cursor: notifSaving ? 'not-allowed' : 'pointer',
                              position: 'relative',
                              transition: 'background-color 0.2s ease',
                              opacity: notifSaving ? 0.7 : 1,
                            }}
                            aria-label={uiText("Make the channel {{v0}}", { v0: c.enabled ? uiText("Disabled") : uiText("Enabled") })}
                          >
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#ffffff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: c.enabled ? '26px' : '2px',
                                transition: 'left 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }}
                            />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleSendTestNotification(c.id)}
                          disabled={notifSaving}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: notifSaving ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: notifSaving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 800,
                          }}
                        >
                          {uiText("Test notification")}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteChannel('threads', c.id)}
                          disabled={notifSaving}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: notifSaving ? '#ccc' : '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: notifSaving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 800,
                          }}
                        >
                          {uiText("Delete")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                </>)}

                {can('notification.slack') && (<>
                <div style={{ marginTop: '14px', fontSize: '12px', color: '#666', fontWeight: 700 }}>{uiText("Slack integration (add a thread notification channel)")}</div>

                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {uiText("After signing in on the Slack screen, select a channel to connect. Depending on your Slack workspace settings, an owner or admin may need to approve the app.")}
                  </div>
                  {slackOauthAwaiting && slackOauthTarget === 'threads' && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {uiText("After linking, this screen updates automatically (it may take a few seconds to appear).")}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => handleSlackInstall('threads')}
                      disabled={slackInstallBusy || notifSaving}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: slackInstallBusy || notifSaving ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: slackInstallBusy || notifSaving ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                      }}
                    >
                      {slackInstallBusy && slackOauthTarget === 'threads' ? uiText("Starting up...") : uiText("Open Slack connection")}
                    </button>
                  </div>
                </div>
                {renderManualWebhookForm('threads')}
                </>)}
                </>)}
                {settingsTab === 'email' && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', fontWeight: 900 }}>
                      {uiText("Initial value for email notifications when posting")}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                      {uiText("Applies to new threads, follow-ups, and comments. The person posting can still change it for that post.")}
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                        {uiText("Notify workspace members by email")}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: emailNotifyDefault ? 'var(--workspace-primary, #cfae91)' : '#6c757d', fontWeight: '500' }}>
                          {emailNotifyDefault ? 'ON' : 'OFF'}
                        </span>
                        <button
                          type="button"
                          onClick={() => applyNotificationRulesPatch({ emailNotifyDefault: !emailNotifyDefault })}
                          disabled={rulesUpdating || notifSaving}
                          aria-busy={rulesUpdating}
                          style={{
                            width: '48px',
                            height: '24px',
                            backgroundColor: emailNotifyDefault ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: (rulesUpdating || notifSaving) ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            transition: 'background-color 0.2s ease',
                            opacity: (rulesUpdating || notifSaving) ? 0.7 : 1,
                          }}
                        >
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: '#ffffff',
                              borderRadius: '50%',
                              position: 'absolute',
                              top: '2px',
                              left: emailNotifyDefault ? '26px' : '2px',
                              transition: 'left 0.2s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '8px',
            backgroundColor: 'transparent',
            color: '#666',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {uiText("←Back")}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;

